import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { captureException } from '@sentry/react';
import ErrorFallback from 'typescript/components/error_fallback';

interface ShopErrorBoundaryProps extends PropsWithChildren {
  appName: string;
  fallback?: ReactNode;
}

interface ShopErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level error boundary for Shop React apps.
 * Catches render-time errors, reports them to both Datadog RUM
 * (via addError, which feeds Error Tracking and supports monitors)
 * and Sentry, then renders a fallback UI so the page doesn't go blank.
 *
 * This must be a class component because React's error boundary API
 * (getDerivedStateFromError / componentDidCatch) has no hook equivalent.
 */
class ShopErrorBoundary extends Component<
  ShopErrorBoundaryProps,
  ShopErrorBoundaryState
> {
  state: ShopErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ShopErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    datadogRum.addError(error, {
      source: 'react-error-boundary',
      appName: this.props.appName,
      pageUrl: window.location.href,
      componentStack: errorInfo.componentStack,
    });

    captureException(error, {
      extra: {
        appName: this.props.appName,
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default ShopErrorBoundary;
